import type {
  HaftkhabisMove,
  HaftkhabisRoundResult,
  HaftkhabisView,
} from "@bing/engine-haftkhabis";
import type { BotDifficulty, CardDeckId, HaftkhabisTarget } from "@bing/shared";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard } from "../components/MatchEndCard";
import { SegmentedChoice } from "../components/SegmentedChoice";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { StartRow } from "../components/StartScreenShell";
import { GAME_SERVER_URL } from "../config";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { HaftkhabisTable } from "../screens/HaftkhabisTable";
import {
  getBotDifficulty,
  getCardDeck,
  getHaftkhabisTarget,
  setBotDifficulty,
  setCardDeck,
  setHaftkhabisTarget,
} from "../settings/prefs";
import { useSession } from "../store/context";
import { CardDeckContext } from "./card-decks";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { useOutcomeSfx } from "./game-sfx";
import { createGameStore } from "./game-store";
import { handKeyOf, useHandDeck } from "./hand-deck";
import { HAFTKHABIS_TARGET_NUMBER_CHOICES } from "./match-targets";
import { heartsSeatFallback } from "./hearts-seat-label";
import { useOverlayActive } from "./lifecycle";
import { presetAvatarFor } from "./seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import { useTableChannelGate } from "./table-channel-gate";
import { useDeckGate } from "./use-card-faces";
import { END_REVEAL_DELAY_MS, useEndReveal } from "./use-end-reveal";
import { useSoloLoad } from "./use-solo-load";

/** After the round's card, a beat before the match's end card takes the screen. */
export const HAFTKHABIS_END_MARGIN_MS = 300;

/**
 * The end of the match: the shared template, each PLAYER a side (no teams), ascending, the FEWEST minus
 * points first, equal totals sharing a place, and a tie for the fewest a tie, exactly بی‌دل's (D19; trap:
 * «بی‌دل IS SCORED DOWNWARD»). Exported for the online game.
 */
export function WinOverlay({
  result,
  totalScores,
  targetScore,
  you,
  lastRound = null,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  result: GameResult;
  /** Off the VIEW: a match the room ends is scored 1 and 0, which is no total. */
  totalScores: readonly number[];
  targetScore: number;
  you: number;
  /** The last finished round: named under the rows. */
  lastRound?: HaftkhabisRoundResult | null;
  self?: SeatIdentity | null;
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  againDisabled?: boolean;
  againHint?: string;
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const t = fa.haftkhabis;
  const seats = totalScores.length;
  const inWinners = result.winners.includes(you);
  const drawn = result.tied === true && inWinners;
  const won = inWinners && !drawn;
  // Who won first, then the fewest: an engine's end sorts the same either way; a room's follows `winners`.
  const ranked = totalScores
    .map((score, seat) => ({ seat, score, won: result.winners.includes(seat) }))
    .sort((a, b) => Number(b.won) - Number(a.won) || a.score - b.score);
  const roomEnded = result.reason === "idle" || result.reason === "resign";
  const ranks = ["①", "②", "③", "④"];
  const rankOf = (score: number): string =>
    ranks[totalScores.filter((s) => s < score).length] ?? "④";
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: t.you,
      fallback: (st) => heartsSeatFallback(st, you, seats, players),
    });
  const avatarOf = (seat: number) => resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
  const roomReason =
    result.reason === "idle"
      ? won
        ? t.endOpponentIdle
        : t.endYouIdle
      : result.reason === "resign" && won
        ? t.endOpponentResigned
        : null;
  return (
    <MatchEndCard
      label={t.gameOver}
      outcome={won ? "won" : drawn ? "drawn" : "lost"}
      glyph="🎴"
      title={won ? t.youWon : drawn ? t.drawn : t.youLost}
      reason={
        <>
          {roomReason !== null ? <div data-testid="hk-end-reason">{roomReason}</div> : null}
          <div>
            {drawn
              ? t.drawnHint(faDigits(totalScores[you] ?? 0))
              : `${t.scores} · ${t.target(targetScore)}`}
          </div>
        </>
      }
      sides={ranked.map((r) => ({
        key: `seat-${r.seat}`,
        mine: r.seat === you,
        mark: roomEnded ? undefined : (
          <span style={{ color: "var(--accent)", fontWeight: 800 }}>{rankOf(r.score)}</span>
        ),
        members: [{ seat: r.seat, name: nameOf(r.seat), avatar: avatarOf(r.seat) }],
        result: <span dir="ltr">{t.minus(r.score)}</span>,
        attrs: { "data-seat": r.seat },
      }))}
      lead={
        lastRound && !roomEnded ? (
          <div data-testid="hk-last-round" style={{ fontSize: 13, color: "var(--hint)" }}>
            {lastRound.winner === you
              ? t.roundWonYou(lastRound.round)
              : t.lastRoundLine(nameOf(lastRound.winner))}
            {lastRound.doubled ? ` · ${t.doubled}` : ""}
          </div>
        ) : null
      }
      promo
      fitWhole
      playAgain={t.playAgain}
      leave={t.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Owns the solo connection + store, and renders connect/error/table/game-over (بی‌دل's shape). */
function HaftkhabisSoloRound({
  difficulty,
  target,
  resumed,
  onAgain,
}: {
  /** FIXED for this game: chosen on the start screen. The server ignores both on a resume. */
  difficulty: BotDifficulty;
  target: HaftkhabisTarget;
  /** A resume stood the start screen aside: the table it lands on is no fresh deal. */
  resumed: boolean;
  onAgain: () => void;
}): ReactNode {
  const t = fa.haftkhabis;
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<HaftkhabisMove, HaftkhabisView>({
          wsUrl: GAME_SERVER_URL,
          room: "haftkhabis",
          getInitData: () => initData,
          difficulty,
          haftkhabisTarget: target,
        }),
      ),
    // `round` bumps to start a fresh game (a new connection) when the channel gate clears
    [initData, round, difficulty, target],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // The end card waits for the round's card, then a beat (a result whose frame is no terminal: the
  // shell's own beat).
  const [holding, setHolding] = useState(false);
  const showEnd = useEndReveal(
    result !== null && !holding,
    view?.phase === "gameOver" ? HAFTKHABIS_END_MARGIN_MS : END_REVEAL_DELAY_MS,
  );
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  const overlayActive = useOverlayActive();
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per round (`hand-deck.ts`): a pick lands at the next deal
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("haftkhabis", view));

  // «خروج»: leave but KEEP the game resumable.
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی»: END it server-side and WAIT for the ack; on failure stay, and offer a retry.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("haftkhabis");
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» returns to the START SCREEN, the same flow every solo game uses.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? t.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {t.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : t.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={t.leave}
      />
    );
  }

  return (
    <CardDeckContext.Provider value={drawDeck}>
      <HaftkhabisTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
        onHolding={setHolding}
        resumed={resumed}
      />
      {result && showEnd ? (
        <WinOverlay
          result={result}
          totalScores={view.totalScores}
          targetScore={view.targetScore}
          you={view.you}
          lastRound={view.lastRound}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/**
 * Owns the START SCREEN → game flow: the limit, the level and the card design, chosen before the deal and
 * FIXED for the game, each remembered per device. A RESUME stands the screen aside.
 */
export function HaftkhabisGame(): ReactNode {
  const t = fa.haftkhabis;
  const setView = useSession((s) => s.setView);
  const [resumed] = useState(() => takeResumeIntent());
  const [phase, setPhase] = useState<"select" | "playing">(resumed ? "playing" : "select");
  const [target, setTarget] = useState<HaftkhabisTarget>(getHaftkhabisTarget());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());
  /** A game after the resumed one is a fresh one. */
  const [round, setRound] = useState(0);

  if (phase === "select") {
    return (
      <SoloStartScreen
        title={t.title}
        emoji="7️⃣"
        heading={fa.soloStart.heading}
        leaveLabel={t.leave}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      >
        <StartRow label={t.chooseTarget}>
          <SegmentedChoice
            label={t.chooseTarget}
            options={HAFTKHABIS_TARGET_NUMBER_CHOICES}
            value={target}
            onChange={(v) => {
              setTarget(v);
              setHaftkhabisTarget(v);
            }}
            compact
          />
        </StartRow>
      </SoloStartScreen>
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered (بی‌دل's note).
  return (
    <CardDeckContext.Provider value={deck}>
      <HaftkhabisSoloRound
        difficulty={difficulty}
        target={target}
        resumed={resumed && round === 0}
        onAgain={() => {
          setRound((r) => r + 1);
          setPhase("select");
        }}
      />
    </CardDeckContext.Provider>
  );
}
